import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PageHero } from "@/components/PageHero";
import {
    Settings as SettingsIcon, Building2, Copy, Check, Loader2, Pause,
    Play, Unplug, Link2, ShieldCheck, User, ArrowRight, History, Eye,
} from "lucide-react";

const PERM_LABELS = {
    timeline: ["Journey timeline", "The full chronological story — behaviors, changes, milestones."],
    behaviors: ["Behavior entries", "Individual behavior notes with dates and details."],
    medications: ["Medication list", "Current and stopped medications, allergies."],
    appointments: ["Appointment summaries", "Visit prep summaries and recent-change notes."],
    questions: ["Saved questions", "Questions you've prepared for providers."],
    documents: ["Document Passport items", "Master switch. Even when on, they only ever open documents you individually share from Document Passport."],
    wellness: ["Caregiver wellness trends", "Your check-in trends only — never your private notes."],
    ai_summaries: ["AI-generated summaries", "Lane's visit summaries and overviews."],
};

export default function Settings() {
    const { user } = useAuth();
    return (
        <div className="space-y-8 animate-fade-in-up" data-testid="settings-page">
            <PageHero
                kicker="Settings"
                kickerIcon={SettingsIcon}
                title="Your account & sharing"
                subtitle="You own this record. Organizations only ever see what you choose to share — and you can change your mind anytime."
            />
            <CareTeamAccess />
            <EmailPreferences />
            <section className="ml-card p-6 bg-white" data-testid="settings-account">
                <h2 className="text-lg font-heading font-semibold text-[#1F2624] flex items-center gap-2">
                    <User size={18} strokeWidth={1.7} /> Account
                </h2>
                <p className="mt-2 text-sm text-[#4C5551]">
                    Signed in as <strong className="text-[#1F2624]">{user?.email}</strong>
                    {user?.name ? ` (${user.name})` : ""}.
                </p>
                <div className="mt-3 flex gap-2 flex-wrap text-sm">
                    <Link to="/intake" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl bg-white border border-cream-dark text-[#4C5551] hover:border-teal transition-colors" data-testid="settings-link-intake">
                        Update care details <ArrowRight size={14} />
                    </Link>
                    <Link to="/privacy" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl bg-white border border-cream-dark text-[#4C5551] hover:border-teal transition-colors" data-testid="settings-link-privacy">
                        <ShieldCheck size={14} /> Privacy policy
                    </Link>
                </div>
                <PasswordSection user={user} />
            </section>
            <AccountData />
        </div>
    );
}

/* ---------------- Your data & devices (Phase 5) ---------------- */
function AccountData() {
    const { logout, refresh } = useAuth();
    const [devices, setDevices] = useState(null);
    const [busy, setBusy] = useState("");
    const [confirmText, setConfirmText] = useState("");
    const [showDelete, setShowDelete] = useState(false);

    useEffect(() => {
        api.get("/me/devices")
            .then(r => setDevices(r.data.devices || []))
            .catch(() => setDevices([]));
    }, []);

    const browserOf = (ua = "") => {
        if (/edg/i.test(ua)) return "Edge";
        if (/chrome|crios/i.test(ua)) return "Chrome";
        if (/firefox|fxios/i.test(ua)) return "Firefox";
        if (/safari/i.test(ua)) return "Safari";
        return ua ? "Browser" : "Unknown device";
    };
    const deviceOf = (ua = "") => (/mobile|iphone|android/i.test(ua) ? "Mobile" : "Desktop");
    const methodLabel = {
        passwordless_code: "6-digit code", passwordless_link: "magic link",
        password: "password", magic_link: "magic link",
    };

    const exportData = async () => {
        setBusy("export");
        try {
            const r = await api.get("/me/export", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `memory-lane-export-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Your data export is downloading.");
        } catch {
            toast.error("Couldn't build your export right now.");
        } finally {
            setBusy("");
        }
    };

    const revokeAll = async () => {
        setBusy("revoke");
        try {
            const r = await api.post("/me/revoke-devices");
            if (r.data?.token) setToken(r.data.token);
            await refresh();
            toast.success("Signed out everywhere else. This device stays signed in.");
        } catch {
            toast.error("Couldn't sign out other devices.");
        } finally {
            setBusy("");
        }
    };

    const deleteAccount = async () => {
        if (confirmText.trim().toUpperCase() !== "DELETE") {
            toast.error('Type DELETE to confirm.');
            return;
        }
        setBusy("delete");
        try {
            await api.post("/me/delete-account", { confirm: "DELETE" });
            toast.success("Your account and data have been deleted. Take care.");
            logout();
            window.location.href = "/";
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't delete your account.");
            setBusy("");
        }
    };

    return (
        <section className="ml-card p-6 bg-white" data-testid="settings-data-devices">
            <h2 className="text-lg font-heading font-semibold text-[#1F2624] flex items-center gap-2">
                <History size={18} strokeWidth={1.7} /> Your data & devices
            </h2>
            <p className="mt-1 text-sm text-[#4C5551]">
                Everything you've entered belongs to you — download it, sign out of old devices, or erase it all.
            </p>

            <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#6E7A74]">Recent sign-in activity</p>
                {devices === null ? (
                    <p className="mt-2 text-sm text-[#6E7A74]">Loading…</p>
                ) : devices.length === 0 ? (
                    <p className="mt-2 text-sm text-[#6E7A74]" data-testid="settings-devices-empty">
                        No sign-in activity recorded yet — it appears from your next sign-in.
                    </p>
                ) : (
                    <ul className="mt-2 divide-y divide-cream-dark/60" data-testid="settings-devices-list">
                        {devices.slice(0, 6).map((d, i) => (
                            <li key={i} className="py-2 flex items-center gap-3 text-sm">
                                <span className="text-[#1F2624] font-medium">
                                    {browserOf(d.user_agent)} · {deviceOf(d.user_agent)}
                                </span>
                                <span className="text-[#6E7A74] text-xs">
                                    {methodLabel[d.method] || d.method || "sign-in"}
                                    {d.at ? ` · ${new Date(d.at).toLocaleString()}` : ""}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" onClick={exportData} disabled={busy === "export"}
                    className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] rounded-2xl bg-white border border-cream-dark text-sm text-[#1F2624] hover:border-teal transition-colors disabled:opacity-50"
                    data-testid="settings-export-data">
                    {busy === "export" ? <Loader2 size={14} className="animate-spin" /> : null}
                    Download my data (JSON)
                </button>
                <button type="button" onClick={revokeAll} disabled={busy === "revoke"}
                    className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] rounded-2xl bg-white border border-cream-dark text-sm text-[#1F2624] hover:border-teal transition-colors disabled:opacity-50"
                    data-testid="settings-revoke-devices">
                    {busy === "revoke" ? <Loader2 size={14} className="animate-spin" /> : null}
                    Sign out everywhere else
                </button>
            </div>

            <div className="mt-6 border-t border-cream-dark pt-4">
                {!showDelete ? (
                    <button type="button" onClick={() => setShowDelete(true)}
                        className="text-sm text-[#C53030] underline underline-offset-2 min-h-[44px]"
                        data-testid="settings-delete-account-open">
                        Delete my account and all data…
                    </button>
                ) : (
                    <div className="rounded-2xl border border-[#C53030]/30 bg-[#C53030]/5 p-4" data-testid="settings-delete-confirm">
                        <p className="text-sm text-[#1F2624] font-medium">This permanently erases your account and everything you've entered.</p>
                        <p className="text-xs text-[#4C5551] mt-1">There's no undo. Consider downloading your data first. Type <strong>DELETE</strong> to confirm.</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <input type="text" value={confirmText} onChange={e => setConfirmText(e.target.value)}
                                placeholder="DELETE" className="ml-input !w-36 text-sm"
                                data-testid="settings-delete-confirm-input" />
                            <button type="button" onClick={deleteAccount} disabled={busy === "delete"}
                                className="px-4 py-2 min-h-[44px] rounded-2xl bg-[#C53030] text-white text-sm font-semibold disabled:opacity-50"
                                data-testid="settings-delete-account-btn">
                                {busy === "delete" ? "Deleting…" : "Permanently delete"}
                            </button>
                            <button type="button" onClick={() => { setShowDelete(false); setConfirmText(""); }}
                                className="px-4 py-2 min-h-[44px] rounded-2xl bg-white border border-cream-dark text-sm"
                                data-testid="settings-delete-cancel">
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

function PasswordSection({ user }) {
    const { refresh } = useAuth();
    const hasPassword = user && user.auth_method !== "email_only";
    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState("");
    const [pw, setPw] = useState("");
    const [busy, setBusy] = useState(false);

    const save = async (e) => {
        e.preventDefault();
        if ((pw || "").length < 8) { toast.error("Password should be at least 8 characters."); return; }
        setBusy(true);
        try {
            const r = await api.post("/me/set-password", {
                password: pw, ...(hasPassword ? { current_password: current } : {}),
            });
            toast.success(r.data.message || "Password saved.");
            setOpen(false); setPw(""); setCurrent("");
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save the password.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="mt-5 border-t border-cream-dark pt-4" data-testid="settings-password-section">
            {!open ? (
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <p className="text-sm text-[#4C5551] max-w-lg">
                        {hasPassword
                            ? <><strong className="text-[#1F2624]">Password is on.</strong> You sign in with your email and password.</>
                            : <><strong className="text-[#1F2624]">No password needed.</strong> You sign in with just your email. Want extra protection? Add an optional password.</>}
                    </p>
                    <button type="button" onClick={() => setOpen(true)} data-testid="settings-password-toggle"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-2xl text-sm bg-white border border-cream-dark text-[#4C5551] hover:border-teal transition-colors">
                        {hasPassword ? "Change password" : "Create a password"}
                    </button>
                </div>
            ) : (
                <form onSubmit={save} className="space-y-2.5 max-w-sm" noValidate>
                    {hasPassword && (
                        <input type="password" data-testid="settings-password-current" value={current}
                            onChange={e => setCurrent(e.target.value)} placeholder="Current password"
                            className="ml-input w-full" autoComplete="current-password" />
                    )}
                    <input type="password" data-testid="settings-password-new" value={pw}
                        onChange={e => setPw(e.target.value)} placeholder="New password (8+ characters)"
                        className="ml-input w-full" autoComplete="new-password" minLength={8} />
                    <div className="flex gap-2">
                        <button type="submit" disabled={busy} data-testid="settings-password-save"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold bg-teal text-white hover:bg-teal-dark disabled:opacity-60">
                            {busy ? "Saving…" : "Save password"}
                        </button>
                        <button type="button" onClick={() => { setOpen(false); setPw(""); setCurrent(""); }}
                            data-testid="settings-password-cancel"
                            className="inline-flex items-center px-4 py-2 rounded-2xl text-sm bg-white border border-cream-dark text-[#4C5551]">
                            Cancel
                        </button>
                    </div>
                    {!hasPassword && (
                        <p className="text-[11px] text-[#6E7A74]">
                            After you set one, you'll sign in with email + password. An emailed sign-in link always works too.
                        </p>
                    )}
                </form>
            )}
        </div>
    );
}

export function EmailPreferences() {
    const [enabled, setEnabled] = useState(null);

    useEffect(() => {
        api.get("/daily-brief/digest-pref").then(r => setEnabled(r.data.enabled)).catch(() => setEnabled(true));
    }, []);

    const toggle = async () => {
        const next = !enabled;
        setEnabled(next);
        try {
            await api.post("/daily-brief/digest-pref", { enabled: next });
            toast.success(next ? "Morning check-in emails on." : "Morning check-in emails off.");
        } catch {
            setEnabled(!next);
            toast.error("Couldn't save that.");
        }
    };

    return (
        <section className="ml-card p-6 bg-white" data-testid="settings-email-prefs">
            <h2 className="text-lg font-heading font-semibold text-[#1F2624]">Email check-ins</h2>
            <div className="mt-3 flex items-start justify-between gap-4">
                <p className="text-sm text-[#4C5551] max-w-lg">
                    <strong className="text-[#1F2624]">Morning check-in email.</strong>{" "}
                    On days you don&apos;t open Memory Lane, Lane sends your daily check-in by email —
                    one short note, never more than one a day.
                </p>
                <button
                    onClick={toggle}
                    disabled={enabled === null}
                    data-testid="settings-digest-toggle"
                    className={`shrink-0 w-12 h-7 rounded-full transition-colors relative ${enabled ? "bg-teal" : "bg-cream-dark"}`}
                    aria-pressed={!!enabled}
                >
                    <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-[left] ${enabled ? "left-[22px]" : "left-0.5"}`} />
                </button>
            </div>
        </section>
    );
}

export function CareTeamAccess() {
    const [data, setData] = useState(null);
    const [invite, setInvite] = useState(null);
    const [inviting, setInviting] = useState(false);
    const [copied, setCopied] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/care-access/mine");
            setData(r.data);
        } catch {
            setData({ connections: [], permission_keys: Object.keys(PERM_LABELS) });
        }
    };
    useEffect(() => { load(); }, []);

    const update = async (orgId, patch) => {
        try {
            const r = await api.put(`/care-access/${orgId}`, patch);
            setData(d => ({ ...d, connections: d.connections.map(c => c.org_id === orgId ? { ...c, ...r.data } : c) }));
            if (patch.status === "paused") toast.success("Access paused.");
            else if (patch.status === "active") toast.success("Access resumed.");
        } catch {
            toast.error("Couldn't update access.");
        }
    };

    // One-tap disconnect: immediate, no reason required. The org's view
    // empties at once and they move back to "waiting to connect".
    const stopSharing = async (c) => {
        if (!window.confirm(`Are you sure? ${c.org_name || "This organization"} will immediately stop seeing everything.`)) return;
        try {
            await api.post(`/care-access/${c.org_id}/revoke`);
            toast.success("Sharing stopped. They can no longer see anything.");
            load();
        } catch {
            toast.error("Couldn't stop sharing — try again.");
        }
    };

    const acceptInvite = async (c) => {
        try {
            await api.post(`/care-access/${c.org_id}/consent`, { accept: true });
            toast.success(`You're sharing with ${c.org_name}.`);
            try { localStorage.removeItem(`mlcn_org_consent_dismissed_${c.org_id}`); } catch { /* ignore */ }
            load();
        } catch {
            toast.error("Couldn't connect — try again.");
        }
    };

    const generateInvite = async () => {
        setInviting(true);
        try {
            const r = await api.post("/care-access/invite");
            setInvite(r.data);
        } catch {
            toast.error("Couldn't create an invitation code.");
        } finally {
            setInviting(false);
        }
    };

    const copyInvite = async () => {
        try {
            await navigator.clipboard.writeText(invite.share_text);
            setCopied(true);
            toast.success("Invitation copied — send it to your organization.");
            setTimeout(() => setCopied(false), 2000);
        } catch { toast.error("Couldn't copy."); }
    };

    const connections = (data?.connections || []).filter(c => c.status === "active" || c.status === "paused");
    const waiting = (data?.connections || []).filter(c => c.status === "invited");

    return (
        <section className="ml-card p-6 bg-white" id="care-team-access" data-testid="care-team-access">
            <h2 className="text-lg font-heading font-semibold text-[#1F2624] flex items-center gap-2">
                <Building2 size={18} strokeWidth={1.7} /> Care Team Access
            </h2>
            <p className="mt-1.5 text-sm text-[#4C5551] max-w-2xl">
                Organizations you've connected can view only what you switch on below. They can never edit or
                delete anything you've entered, and everything starts OFF.
            </p>

            {waiting.length > 0 && (
                <div className="mt-4 space-y-3">
                    {waiting.map(c => (
                        <div key={c.org_id} className="rounded-2xl border border-dashed border-[#CBD5E0] bg-cream/50 p-5 flex items-center justify-between gap-3 flex-wrap"
                            data-testid={`care-access-waiting-${c.org_id}`}>
                            <div>
                                <p className="text-sm font-semibold text-[#1F2624]">{c.org_name || "Your care team"} is waiting to connect</p>
                                <p className="text-xs text-[#6E7A74] mt-0.5">
                                    They can&apos;t see anything yet. If you connect, they&apos;d see your check-ins, behavior
                                    logs, and summaries — read-only. Lane conversations stay private.
                                </p>
                            </div>
                            <button type="button" onClick={() => acceptInvite(c)} data-testid={`care-access-accept-${c.org_id}`}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold bg-teal text-white hover:bg-teal-dark">
                                Share with my care team
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {data === null ? (
                <p className="mt-4 text-sm text-[#6E7A74]">Loading your connections…</p>
            ) : connections.length === 0 ? (
                waiting.length === 0 && <p className="mt-4 text-sm text-[#4C5551]" data-testid="care-access-empty">
                    No organizations are connected yet. When your GUIDE program, clinic, or home-health agency
                    joins Memory Lane, invite them below.
                </p>
            ) : (
                <div className="mt-4 space-y-4">
                    {connections.map(c => (
                        <div key={c.org_id} className="border border-cream-dark rounded-2xl p-5" data-testid={`care-access-org-${c.org_id}`}>
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div>
                                    <p className="text-sm font-semibold text-[#1F2624]">Sharing with {c.org_name || "Organization"}</p>
                                    <p className="text-xs text-[#6E7A74] mt-0.5">
                                        {c.status === "paused" ? "Access paused" : "Connected"}
                                        {c.connected_at ? ` · since ${new Date(c.connected_at).toLocaleDateString()}` : ""}
                                        {" · "}{Object.values(c.permissions || {}).filter(Boolean).length} of {Object.keys(PERM_LABELS).length} items shared
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    {c.status === "paused" ? (
                                        <button type="button" onClick={() => update(c.org_id, { status: "active" })} data-testid={`care-access-resume-${c.org_id}`}
                                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-2xl text-xs font-semibold bg-teal text-white">
                                            <Play size={12} /> Resume
                                        </button>
                                    ) : (
                                        <button type="button" onClick={() => update(c.org_id, { status: "paused" })} data-testid={`care-access-pause-${c.org_id}`}
                                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-2xl text-xs font-semibold bg-white border border-cream-dark text-[#4C5551] hover:border-teal">
                                            <Pause size={12} /> Pause access
                                        </button>
                                    )}
                                    <button type="button" onClick={() => stopSharing(c)}
                                        data-testid={`care-access-disconnect-${c.org_id}`}
                                        className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-2xl text-xs font-semibold bg-white border border-coral/40 text-coral-dark hover:bg-coral-light/30">
                                        <Unplug size={12} /> Stop sharing
                                    </button>
                                </div>
                            </div>
                            <div className="mt-4 grid sm:grid-cols-2 gap-2.5">
                                {Object.entries(PERM_LABELS).map(([k, [label, help]]) => {
                                    const on = !!(c.permissions || {})[k];
                                    return (
                                        <button key={k} type="button" data-testid={`care-access-toggle-${k}-${c.org_id}`}
                                            onClick={() => update(c.org_id, { permissions: { [k]: !on } })}
                                            className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${on ? "border-teal bg-teal-soft/40" : "border-cream-dark bg-white hover:border-teal/40"}`}>
                                            <span className={`mt-0.5 w-9 h-5 rounded-full relative shrink-0 transition-colors ${on ? "bg-teal" : "bg-cream-dark"}`}>
                                                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
                                            </span>
                                            <span className="min-w-0">
                                                <span className="block text-sm font-medium text-[#1F2624]">{label}</span>
                                                <span className="block text-xs text-[#6E7A74] mt-0.5">{help}</span>
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-5 border-t border-cream-dark pt-5">
                <p className="text-sm font-semibold text-[#1F2624]">Invite an organization</p>
                <p className="text-xs text-[#6E7A74] mt-0.5">Generate a one-time code your GUIDE program, clinic, or agency enters in their Memory Lane workspace.</p>
                {invite ? (
                    <div className="mt-3 rounded-2xl border border-teal/30 bg-teal-soft/30 p-4" data-testid="care-access-invite-result">
                        <p className="text-lg font-mono font-semibold text-teal-dark tracking-widest">{invite.code}</p>
                        <p className="text-xs text-[#4C5551] mt-1">{invite.share_text}</p>
                        <button type="button" onClick={copyInvite} data-testid="care-access-invite-copy"
                            className="mt-2.5 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-2xl text-xs font-semibold bg-white border border-cream-dark text-[#4C5551] hover:border-teal">
                            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy invitation"}
                        </button>
                    </div>
                ) : (
                    <button type="button" onClick={generateInvite} disabled={inviting} data-testid="care-access-invite-btn"
                        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-sm font-semibold bg-teal text-white hover:bg-teal-dark disabled:opacity-60">
                        {inviting ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Generate invitation code
                    </button>
                )}
            </div>

            <AccessHistory />
        </section>
    );
}

function AccessHistory() {
    const [open, setOpen] = useState(false);
    const [events, setEvents] = useState(null);

    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (next && events === null) {
            try {
                const r = await api.get("/care-access/audit");
                setEvents(r.data.events || []);
            } catch {
                setEvents([]);
            }
        }
    };

    return (
        <div className="mt-5 border-t border-cream-dark pt-5">
            <button type="button" onClick={toggle} data-testid="care-access-history-toggle"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F2624] hover:text-teal-dark">
                <History size={15} strokeWidth={1.8} /> Access history
                <span className="text-xs font-normal text-[#6E7A74]">— every change and view of your record</span>
            </button>
            {open && (
                <div className="mt-3" data-testid="care-access-history-list">
                    {events === null ? (
                        <p className="text-xs text-[#6E7A74]">Loading…</p>
                    ) : events.length === 0 ? (
                        <p className="text-xs text-[#6E7A74]" data-testid="care-access-history-empty">
                            No access events yet. When you change sharing, or someone views your record or a link you shared, it shows up here.
                        </p>
                    ) : (
                        <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
                            {events.map(e => (
                                <li key={e.id} className="flex items-start gap-2.5 rounded-xl border border-cream-dark bg-white p-2.5" data-testid={`care-access-event-${e.id}`}>
                                    <Eye size={13} className={`mt-0.5 shrink-0 ${e.kind === "org_viewed" || e.kind === "share_link_viewed" ? "text-ml_blue" : "text-teal"}`} strokeWidth={1.8} />
                                    <div className="min-w-0">
                                        <p className="text-xs text-[#1F2624]">{e.detail}</p>
                                        <p className="text-[11px] text-[#6E7A74] mt-0.5">{e.at ? new Date(e.at).toLocaleString() : ""}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
